import React, { useState, useEffect } from 'react';
import { listDirectory } from '../../api';
import { useNavigate } from 'react-router';
import {
  FolderOutlined,
  FileOutlined,
  HomeOutlined,
  ArrowLeftOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import { 
  Button, 
  Table, 
  message, 
  Breadcrumb, 
  Space, 
  Badge,
} from 'antd';
import './DirectoryView.css';

const DirectoryView = () => {
  const [currentPath, setCurrentPath] = useState('');
  const [basePath, setBasePath] = useState('');
  const [contents, setContents] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const loadDirectory = async path => {
    try {
      setLoading(true);
      const data = await listDirectory(path || '.');
      setContents(data.contents);
      setCurrentPath(data.currentPath);
      setBasePath(data.basePath || 'Root');
    } catch (err) {
      message.error(
        err.response?.data?.error || 'Failed to load directory contents'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDirectory(currentPath);
  }, [currentPath]);

  const handleItemClick = item => {
    if (item.isDirectory) {
      setCurrentPath(item.path);
      loadDirectory(item.path);
    } else if (item.isAllowedFile) {
      if (item.fileId) {
        const filepath = item.path;
        navigate(`/analysis?filepath=${encodeURIComponent(filepath)}`);
      } else {
        message.info('This file has not been analyzed yet, please try later.');
      }
    }
  };

  const handleBackClick = () => {
    if (currentPath === '' || currentPath === '.') {
      return; // Already at root
    }

    const pathParts = currentPath.split('/').filter(part => part !== '');
    if (pathParts.length > 0) {
      pathParts.pop();
      const parentPath = pathParts.length > 0 ? pathParts.join('/') : '';
      setCurrentPath(parentPath);
      loadDirectory(parentPath);
    }
  };

  const handleHomeClick = () => {
    setCurrentPath('');
    loadDirectory('.');
  };

  const handleBreadcrumbClick = targetPath => {
    setCurrentPath(targetPath);
    loadDirectory(targetPath || '.');
  };

  const formatFileSize = bytes => {
    if (bytes === null || bytes === undefined) return '';
    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }
    return `${size.toFixed(1)} ${units[unitIndex]}`;
  };

  const formatDate = timestamp => {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getBreadcrumbItems = () => {
    const pathParts =
      currentPath === ''
        ? []
        : currentPath.split('/').filter(part => part !== '');
    const items = [
      {
        title: (
          <Space>
            <HomeOutlined />
            <span>{basePath}</span>
          </Space>
        ),
        onClick: handleHomeClick,
        className: 'breadcrumb-home',
      },
    ];

    pathParts.forEach((part, index) => {
      const path = pathParts.slice(0, index + 1).join('/');
      items.push({
        title: part,
        onClick: () => handleBreadcrumbClick(path),
        className: 'breadcrumb-item',
      });
    });

    return items;
  };

  const getStatusBadge = (item) => {
    if (!item.isAllowedFile) return null;
    
    if (item.isAnalyzed) {
      return <Badge status="success" text="Analyzed" />;
    } else if (item.fileId) {
      return <Badge status="processing" text="In Progress" />;
    } else {
      return <Badge status="default" text="Not Analyzed" />;
    }
  };

  const getFileIcon = (record) => {
    if (record.isAnalyzed) {
      return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
    } else if (record.fileId) {
      return <ClockCircleOutlined style={{ color: '#faad14' }} />;
    }
    return <FileOutlined />;
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <div className="file-item" onClick={() => handleItemClick(record)}>
          <Space>
            {record.isDirectory ? (
              <FolderOutlined className="folder-icon" />
            ) : (
              getFileIcon(record)
            )}
            <span className={record.isDirectory ? 'folder-name' : 'file-name'}>
              {text}
            </span>
            {getStatusBadge(record)}
          </Space>
        </div>
      ),
    },
    {
      title: 'Size',
      dataIndex: 'size',
      key: 'size',
      width: 120,
      render: size => formatFileSize(size),
    },
    {
      title: 'Type',
      key: 'type',
      width: 120,
      render: (_, record) => {
        if (record.isDirectory) return 'Folder';
        if (record.isAllowedFile) return 'Allowed File';
        return 'File';
      },
    },
    {
      title: 'Modified Date',
      dataIndex: 'modified',
      key: 'modified',
      width: 180,
      render: timestamp => formatDate(timestamp),
    },
    {
      title: 'Action',
      key: 'action',
      width: 120,
      render: (_, record) => (
        <Button
          type="text"
          size="small"
          onClick={e => {
            e.stopPropagation();
            handleItemClick(record);
          }}
        >
          {record.isDirectory ? 'Open' : record.fileId ? 'Analyze' : 'View'}
        </Button>
      ),
    },
  ];

  const dataSource = contents.map((item, index) => ({
    ...item,
    key: index,
  }));

  return (
    <div className="directory-view">
      <div className="directory-header">
        <div className="navigation-controls">
          <Space>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={handleBackClick}
              disabled={currentPath === '' || currentPath === '.'}
            >
              Back
            </Button>
            <Breadcrumb
              items={getBreadcrumbItems()}
              className="path-breadcrumb"
            />
          </Space>
        </div>
        <div className="file-info">
          <span>All Files ({contents.length})</span>
        </div>
      </div>

      <div className="directory-content">
        <Table
          columns={columns}
          dataSource={dataSource}
          loading={loading}
          pagination={{
            pageSize: 10,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
          }}
          className="directory-table"
          rowClassName={record =>
            record.isDirectory ? 'folder-row' : 'file-row'
          }
        />
      </div>
    </div>
  );
};

export default DirectoryView; 